import { useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { GridApi, ColumnApi, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import he from 'he';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
import MessageBubble, { CHAT_LAYOUT } from './MessageBubble';
import { fetchMessagesPage, MESSAGES_PAGE_SIZE } from '../../hooks/useConversationMessages';
import { useResourceViewerStore } from '../../stores/resourceViewerStore';
import type { ChatMessage } from '../../types/resource';

interface Props {
  projectId: string;
  collectionId: string;
  resourceId: string;
  // Conversation anchor message id — the resource's own chatMessageId (four chat types) or a
  // selected conversation's most-recent message id (phone-image).
  messageId: string;
  // GetMessageIndex result — row to center and highlight on open.
  focusIndex?: number | null;
  onSelectionChange?: (rows: ChatMessage[]) => void;
  // Expose the GridApi so the parent can read row 0 (Save Entire Conversation).
  // `teardown` marks the call as this instance unmounting, so the parent can ignore a
  // late null from a grid it no longer holds.
  onGridApi?: (api: GridApi | null, teardown?: boolean) => void;
}

const DEFAULT_ROW_HEIGHT = 80; // placeholder rows before their block loads

// Row heights come from this getRowHeight callback (honored by the serverSide model this grid
// uses — see below). It mirrors Angular's ResourceController.getRowHeight: measure the text
// offscreen, then add the SAME fixed heights the bubble pins its attachments to (CHAT_LAYOUT),
// so the row height ag-Grid reserves matches the rendered bubble exactly.

// Measure wrapped text at the bubble's content width using a hidden element styled like
// .iio-chat-bubble (so padding + line-height + wrapping match). The returned height includes
// the bubble's vertical padding. Returns 0 for empty text.
function measureBubble(text: string, contentWidth: number): number {
  if (!text) return 0;
  const el = document.createElement('div');
  el.style.cssText =
    `position:absolute;visibility:hidden;left:-9999px;top:0;box-sizing:border-box;` +
    `max-width:${contentWidth}px;padding:10px ${CHAT_LAYOUT.bubblePadH}px;white-space:pre-wrap;` +
    `word-break:break-word;line-height:${CHAT_LAYOUT.lineHeight};font-size:${CHAT_LAYOUT.fontSize}px;` +
    `font-family:"Helvetica Neue","Segoe UI",Arial,sans-serif`;
  el.textContent = text;
  document.body.appendChild(el);
  const h = el.offsetHeight;
  el.remove();
  return h;
}

export default function ConversationGrid({
  projectId,
  collectionId,
  resourceId,
  messageId,
  focusIndex,
  onSelectionChange,
  onGridApi,
}: Props) {
  const openResource = useResourceViewerStore((s) => s.open);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<GridApi | null>(null);
  const columnApiRef = useRef<ColumnApi | null>(null);
  const highlightRef = useRef<{ index: number; on: boolean }>({ index: -1, on: false });
  const focusDoneRef = useRef(false);
  const focusIndexRef = useRef(focusIndex);
  const loadedRef = useRef(false);

  const getRowHeight = useCallback((params: { data?: ChatMessage }): number => {
    const data = params.data;
    if (!data) return DEFAULT_ROW_HEIGHT;
    // Bubble content width = 70% of the column, minus horizontal padding (Angular used the
    // first column's actual width; the wrapper width is equivalent for our single column).
    const colWidth =
      columnApiRef.current?.getAllColumns()?.[0]?.getActualWidth() ??
      wrapperRef.current?.clientWidth ??
      600;
    // Bubble text width mirrors the render: row content (colWidth minus the row's own
    // padding) → bubble max-width 70% → minus the bubble's own horizontal padding.
    const contentWidth = Math.max(
      80,
      (colWidth - CHAT_LAYOUT.rowPadH) * CHAT_LAYOUT.maxWidthPct - CHAT_LAYOUT.bubblePadH * 2,
    );
    const text = data.text ? he.decode(data.text).trim() : '';
    const attachments = data.attachments ?? [];

    let height = CHAT_LAYOUT.rowPadV + CHAT_LAYOUT.metaH;
    if (text) height += measureBubble(text, contentWidth);
    else if (attachments.length === 0) height += CHAT_LAYOUT.missingH;

    for (const a of attachments) {
      const audio = !!a.contentType?.includes('audio');
      const video = !!a.contentType?.includes('video');
      const isFile = a.infected || (!audio && !video && !a.imageType);
      height += CHAT_LAYOUT.attachGap + (isFile ? CHAT_LAYOUT.fileH : audio ? CHAT_LAYOUT.audioH : CHAT_LAYOUT.imageH);
    }
    return Math.ceil(height);
  }, []);

  // A class, not getRowStyle: the theme forces row backgrounds transparent with !important.
  const getRowClass = useCallback((params: { rowIndex: number }) => {
    const h = highlightRef.current;
    return h.on && params.rowIndex === h.index ? 'iio-message-focus' : '';
  }, []);

  // Enterprise serverSide row model (not community infinite): this is the model Angular used
  // (auto-row-height="true"), and it's the one that honors getRowHeight for variable-height
  // rows. Enterprise features are registered globally in scripts/ag-grid-init.ts.
  const datasource: IServerSideDatasource = {
    getRows: async (params: IServerSideGetRowsParams) => {
      try {
        const page = Math.floor(params.request.startRow / MESSAGES_PAGE_SIZE);
        const res = await fetchMessagesPage(projectId, collectionId, resourceId, messageId, page);
        // lastRow = known total so the grid stops requesting past the end.
        params.successCallback(res.messages, res.total);
        loadedRef.current = true;
        window.setTimeout(tryFocus);
      } catch {
        params.failCallback();
      }
    },
  };

  // Center + highlight the focused message once its block has loaded. ag-Grid keeps the first
  // datasource it's given, so the index is read through a ref (it usually arrives after the
  // first block). Scrolling to an unloaded row makes the grid fetch its block, which calls back
  // here; scrolling again then lands on the real row heights.
  function tryFocus() {
    const api = apiRef.current;
    const index = focusIndexRef.current;
    if (!api || !loadedRef.current || focusDoneRef.current || index == null) return;
    api.ensureIndexVisible(index, 'middle');
    if (!api.getDisplayedRowAtIndex(index)?.data) return;
    focusDoneRef.current = true;
    highlightRef.current = { index, on: true };
    api.redrawRows();
  }

  useEffect(() => {
    focusIndexRef.current = focusIndex;
    focusDoneRef.current = false;
    tryFocus();
  }, [focusIndex]);

  // Clear the parent's api handle only on real unmount — deps [] so a changing onGridApi
  // identity can't retrigger this and null the handle mid-session. Hand back our own api:
  // on a keyed remount this cleanup runs in the passive flush, AFTER the new grid's
  // onGridReady, so the parent needs the identity to avoid clobbering the live handle.
  const onGridApiRef = useRef(onGridApi);
  onGridApiRef.current = onGridApi;
  useEffect(() => () => onGridApiRef.current?.(apiRef.current, true), []);

  return (
    <div ref={wrapperRef} className="ag-theme-bootstrap iio-messages-grid" style={{ height: '100%', width: '100%' }} data-testid="viewer-message-grid">
      <AgGridReact
        rowModelType="serverSide"
        serverSideDatasource={datasource}
        cacheBlockSize={MESSAGES_PAGE_SIZE}
        rowHeight={DEFAULT_ROW_HEIGHT}
        rowSelection="multiple"
        // Select messages by clicking (matches Angular) — no checkbox column, so the cell is
        // full-width and the from-me/them alignment works.
        rowMultiSelectWithClick
        suppressContextMenu
        suppressCellSelection
        headerHeight={0}
        getRowHeight={getRowHeight}
        getRowClass={getRowClass}
        columnDefs={[
          {
            colId: 'message',
            field: 'text',
            flex: 1,
            sortable: false,
            resizable: false,
            cellRendererFramework: MessageBubble,
            cellRendererParams: {
              // Opening the message's own resource exits conversation mode (store.open),
              // mirroring Angular's showResourceForSelectedMessage → LaunchFullView.
              onViewResource: (m: ChatMessage) =>
                openResource({ projectId, collectionId, resourceId: m.resourceId }),
            },
            cellStyle: { whiteSpace: 'normal', lineHeight: 'normal' },
          },
        ]}
        onGridReady={({ api, columnApi }) => {
          apiRef.current = api;
          columnApiRef.current = columnApi;
          onGridApi?.(api, false);
        }}
        // The first click in the grid clears the focus highlight.
        onRowClicked={({ api }) => {
          const h = highlightRef.current;
          if (!h.on) return;
          highlightRef.current = { ...h, on: false };
          const node = api.getDisplayedRowAtIndex(h.index);
          if (node) api.redrawRows({ rowNodes: [node] });
        }}
        onSelectionChanged={({ api }) => onSelectionChange?.(api.getSelectedRows() as ChatMessage[])}
      />
    </div>
  );
}
